/**
 * THE SHADCN HALF OF THE TOAST SEAM: antd's imperative `message` /
 * `notification` surface, rendered by Sonner.
 *
 * Read `../index.ts` first — it is the dispatcher, and it says why this is a
 * call-time decision rather than a rendered seam. This file is only the
 * mapping, and it holds no state beyond the engine handle in `./engine`.
 *
 * The mapping is where every behaviour-preserving decision lives, so each one
 * is stated rather than left to be re-derived from the code:
 *
 *  - DURATION UNITS DIFFER. antd counts SECONDS, defaults to 3 for `message`
 *    and 4.5 for `notification`, and treats `0` as "never auto-close". Sonner
 *    counts MILLISECONDS and spells never as `Infinity`. `toMs` is that
 *    conversion, and the two defaults are kept apart on purpose: collapsing
 *    them to one number would quietly shorten every advisory notification.
 *  - `key` IS AN IDENTITY, AND SONNER HAS THE SAME ONE. antd uses `key` to
 *    dedupe, to UPDATE a live notice (the `message.loading` → `message.success`
 *    progress pattern in `RoadsNotTaken`) and to address `destroy(key)`.
 *    Sonner's `id` does all three, so it is a direct map.
 *  - `onClose` FIRES ONCE. antd calls it however the notice went away; Sonner
 *    splits that into `onDismiss` (manual or programmatic) and `onAutoClose`
 *    (the timer) and fires exactly one of them, so passing the same handler to
 *    both reproduces antd without ever double-calling.
 *  - THE RETURN VALUE IS CALLABLE AND THENABLE. antd's `MessageType` is a
 *    function that hides the notice, with promise methods bolted on.
 *    `const hide = message.loading(t('…'), 0)` then `hide()` is live in two
 *    places (`tables/unloading-events`, `insights/ViewDashboardDetail`), so
 *    `makeMessageType` preserves both shapes.
 *  - CONTENT IS A ReactNode, NOT A STRING. Sonner renders the node as the
 *    toast title, so the error handler's
 *    `<span style={{ whiteSpace: 'pre-line' }}>` (PRO-193, multi-line server
 *    messages) and the `<T>` elements every call site passes render exactly as
 *    they did. That is also what keeps the localized-error contract intact:
 *    this file never inspects, formats or substitutes a message — whatever
 *    `extract(err, t) ?? t('generic…')` produced is what Sonner is handed.
 *  - PLACEMENT IS PER-API, BECAUSE IT IS IN antd. antd's `message` is
 *    top-centre and its `notification` is a top-RIGHT card. Sonner takes a
 *    per-toast `position`, so `notification.*` asks for `top-right` and
 *    `message.*` inherits the host's `top-center`. The two therefore land
 *    where they always did rather than stacking together.
 *  - `btn` IS AN ACTION. antd's notification action slot is a node (Live Ops'
 *    undo button); Sonner's `action` accepts one.
 *  - antd HAS TWO HOLDERS AND SONNER HAS ONE STORE. `message.destroy()` with
 *    no key empties antd's message holder and leaves every notification card
 *    standing (Live Ops' 8s undo is one), while `toast.dismiss()` with no id
 *    empties everything Sonner is showing. So this file remembers which API
 *    emitted which id and a bare `destroy()` dismisses only that API's own —
 *    the two holders, reproduced. A KEYED `destroy(key)` still addresses the
 *    id whichever API emitted it, which is the only form any call site uses.
 */
import { isValidElement } from 'react'
import type { CSSProperties, ReactNode } from 'react'

import {
  getSonnerToast,
  type SonnerToast,
  type SonnerToastId,
  type SonnerToastOptions,
} from './engine'

export type NoticeType = 'info' | 'success' | 'error' | 'warning' | 'loading'

/**
 * The antd config-object form of `message.*` / `message.open` and of
 * `notification.*`. A superset of what the app passes, so the shim is drop-in;
 * an antd field nothing here uses is simply ignored rather than mistranslated.
 */
export interface ArgsProps {
  /** `message`'s body. */
  content?: ReactNode
  /** `notification`'s title — antd names this field `message`. */
  message?: ReactNode
  /** `notification`'s body line; honoured on a `message` config too. */
  description?: ReactNode
  /** SECONDS. `0`/`null` = never auto-close. Omitted = the antd default. */
  duration?: number | null
  /** Dedupe / update / `destroy(key)` handle → Sonner's `id`. */
  key?: string | number
  type?: NoticeType
  icon?: ReactNode
  onClose?: () => void
  className?: string
  style?: CSSProperties
  /** antd notification's action slot (typically a button) → Sonner `action`. */
  btn?: ReactNode
}

export type JointContent = ReactNode | ArgsProps

/**
 * antd's return value: callable to hide the notice, and thenable. Both shapes
 * are load-bearing at live call sites; see the header.
 */
export interface MessageType {
  (): void
  then: Promise<boolean>['then']
  catch: Promise<boolean>['catch']
  finally: Promise<boolean>['finally']
}

/** antd's positional call form: `(content, duration?, onClose?)`. */
export type PositionalArgs = [
  content: JointContent,
  duration?: number | (() => void),
  onClose?: () => void
]

/** antd's own defaults, in seconds. Different per API — deliberately. */
const MESSAGE_DEFAULT_SECONDS = 3
const NOTIFICATION_DEFAULT_SECONDS = 4.5

const toMs = (
  duration: number | null | undefined,
  fallback: number
): number => {
  if (duration === undefined) return fallback * 1000
  if (duration === null || duration === 0) return Infinity
  return duration * 1000
}

/**
 * A plain object that is neither a React element nor an array is antd's config
 * form; anything else is positional content. `isValidElement` is what keeps a
 * `<T _str="…" />` from being read as a config bag.
 */
const isConfig = (content: JointContent): content is ArgsProps =>
  typeof content === 'object' &&
  content !== null &&
  !isValidElement(content) &&
  !Array.isArray(content)

const makeMessageType = (
  toast: SonnerToast,
  id: SonnerToastId
): MessageType => {
  const hide = (() => {
    toast.dismiss(id)
  }) as MessageType
  // antd resolves its thenable when the notice closes; nothing here awaits the
  // close, only the call, so an already-resolved promise is the honest shape.
  const resolved = Promise.resolve(true)
  hide.then = resolved.then.bind(resolved)
  hide.catch = resolved.catch.bind(resolved)
  hide.finally = resolved.finally.bind(resolved)
  return hide
}

/**
 * antd's two holders. An id is added when its API emits and dropped when the
 * toast closes — Sonner fires exactly one of `onDismiss`/`onAutoClose`, so the
 * one callback both slots share is where the drop happens, and neither set
 * grows over a long session.
 */
type ToastApi = 'message' | 'notification'

const emittedIds: Record<ToastApi, Set<SonnerToastId>> = {
  message: new Set(),
  notification: new Set(),
}

/** Tests only: the sets are module state and outlive a swapped engine handle. */
export const resetEmittedToastIdsForTests = (): void => {
  emittedIds.message.clear()
  emittedIds.notification.clear()
}

const emitTyped = (
  toast: SonnerToast,
  type: NoticeType | undefined,
  title: ReactNode,
  opts: SonnerToastOptions
): SonnerToastId => {
  switch (type) {
    case 'success':
      return toast.success(title, opts)
    case 'error':
      return toast.error(title, opts)
    case 'warning':
      return toast.warning(title, opts)
    case 'info':
      return toast.info(title, opts)
    case 'loading':
      return toast.loading(title, opts)
    default:
      // antd's `message.open` with no `type` is an icon-less notice.
      return toast(title, opts)
  }
}

const optsFromConfig = (
  cfg: ArgsProps,
  defaultSeconds: number
): SonnerToastOptions => ({
  id: cfg.key,
  duration: toMs(cfg.duration, defaultSeconds),
  description: cfg.description,
  icon: cfg.icon,
  className: cfg.className,
  style: cfg.style,
  action: cfg.btn,
})

/**
 * The one place a toast is emitted, so the one place its id is remembered.
 * `settle` is deliberately ONE function in both close slots: Sonner calls
 * exactly one of them, so the caller's `onClose` still fires exactly once, and
 * the two slots stay identical the way antd's single `onClose` is.
 */
const emitTracked = (
  toast: SonnerToast,
  api: ToastApi,
  type: NoticeType | undefined,
  title: ReactNode,
  opts: SonnerToastOptions,
  onClose: (() => void) | undefined
): SonnerToastId => {
  let id: SonnerToastId | undefined
  const settle = () => {
    if (id !== undefined) emittedIds[api].delete(id)
    onClose?.()
  }
  id = emitTyped(toast, type, title, {
    ...opts,
    onDismiss: settle,
    onAutoClose: settle,
  })
  emittedIds[api].add(id)
  return id
}

/** `message.*`: the positional form and the config form share one dispatch. */
const emitMessage = (
  toast: SonnerToast,
  defaultType: NoticeType | undefined,
  content: JointContent,
  duration?: number | (() => void),
  onClose?: () => void
): MessageType => {
  if (isConfig(content)) {
    return makeMessageType(
      toast,
      emitTracked(
        toast,
        'message',
        content.type ?? defaultType,
        content.content,
        optsFromConfig(content, MESSAGE_DEFAULT_SECONDS),
        content.onClose
      )
    )
  }
  // antd accepts `(content, duration, onClose)` and `(content, onClose)`.
  const seconds = typeof duration === 'number' ? duration : undefined
  const close = typeof duration === 'function' ? duration : onClose
  return makeMessageType(
    toast,
    emitTracked(
      toast,
      'message',
      defaultType,
      content,
      { duration: toMs(seconds, MESSAGE_DEFAULT_SECONDS) },
      close
    )
  )
}

/**
 * antd's `notification` is a top-RIGHT card with a title, a body and a close
 * ✕; `message` is a top-centre line with none of those. Sonner takes both as
 * per-toast options, so the two keep their own corners and their own
 * affordances instead of one stack wearing both meanings. The ✕ is not
 * decoration: a notification here can carry an action and an 8-second life
 * (Live Ops' undo), and antd has always let the reader close it early.
 */
const NOTIFICATION_POSITION = 'top-right' as const

const emitNotification = (
  toast: SonnerToast,
  type: NoticeType | undefined,
  config: ArgsProps
): MessageType =>
  makeMessageType(
    toast,
    emitTracked(
      toast,
      'notification',
      type,
      config.message ?? config.content,
      {
        ...optsFromConfig(config, NOTIFICATION_DEFAULT_SECONDS),
        position: NOTIFICATION_POSITION,
        closeButton: true,
      },
      config.onClose
    )
  )

/** The subset of antd's `message` instance the app calls. */
export interface SonnerMessageApi {
  success: (...args: PositionalArgs) => MessageType
  error: (...args: PositionalArgs) => MessageType
  info: (...args: PositionalArgs) => MessageType
  warning: (...args: PositionalArgs) => MessageType
  loading: (...args: PositionalArgs) => MessageType
  open: (config: ArgsProps) => MessageType
  destroy: (key?: string | number) => void
}

/** The subset of antd's `notification` instance the app calls. */
export interface SonnerNotificationApi {
  success: (config: ArgsProps) => MessageType
  error: (config: ArgsProps) => MessageType
  info: (config: ArgsProps) => MessageType
  warning: (config: ArgsProps) => MessageType
  open: (config: ArgsProps) => MessageType
  destroy: (key?: string | number) => void
}

/**
 * Every method resolves the engine at CALL time and returns `null` when the
 * chunk has not landed, so `../index.ts` can fall through to antd rather than
 * swallowing the call. `requireToast` is the one place that decision is taken.
 */
const withToast =
  <A extends unknown[]>(run: (toast: SonnerToast, ...args: A) => MessageType) =>
  (...args: A): MessageType | null => {
    const toast = getSonnerToast()
    return toast ? run(toast, ...args) : null
  }

/**
 * `null` = engine not loaded, so the dispatcher should use antd instead.
 *
 * A KEYED destroy addresses that one id whichever API emitted it — antd's
 * `destroy(key)` behaves the same and every live call site is keyed. A BARE
 * destroy is the holder-scoped one: it may only reach what THIS API emitted,
 * or `message.destroy()` would take down an open notification card that antd
 * would have left standing.
 */
const dismissFor =
  (api: ToastApi) =>
  (key?: string | number): null | undefined => {
    const toast = getSonnerToast()
    if (!toast) return null
    if (key === undefined) {
      emittedIds[api].forEach((id) => toast.dismiss(id))
      emittedIds[api].clear()
    } else {
      toast.dismiss(key)
      emittedIds.message.delete(key)
      emittedIds.notification.delete(key)
    }
    return undefined
  }

/**
 * The `key` a call carries, if it carries one — read by `../index.ts` so a
 * keyed Sonner emit can clear the antd notice the pre-engine fallback may have
 * painted under the same key.
 */
export const noticeKeyOf = (
  args: readonly unknown[]
): string | number | undefined => {
  const [first] = args
  return first !== undefined && isConfig(first as JointContent)
    ? (first as ArgsProps).key
    : undefined
}

/**
 * The Sonner-backed pair, in the shape `../index.ts` dispatches to: EVERY
 * method returns `null` exactly when the engine is not loaded yet, and that
 * one sentinel is the dispatcher's whole signal to use the antd notice
 * instead. Nothing else in either object ever returns `null`.
 */
export const sonnerMessage = {
  success: withToast((t, ...a: PositionalArgs) =>
    emitMessage(t, 'success', ...a)
  ),
  error: withToast((t, ...a: PositionalArgs) => emitMessage(t, 'error', ...a)),
  info: withToast((t, ...a: PositionalArgs) => emitMessage(t, 'info', ...a)),
  warning: withToast((t, ...a: PositionalArgs) =>
    emitMessage(t, 'warning', ...a)
  ),
  loading: withToast((t, ...a: PositionalArgs) =>
    emitMessage(t, 'loading', ...a)
  ),
  open: withToast((t, config: ArgsProps) =>
    emitMessage(t, config.type, config)
  ),
  destroy: dismissFor('message'),
}

export const sonnerNotification = {
  success: withToast((t, c: ArgsProps) => emitNotification(t, 'success', c)),
  error: withToast((t, c: ArgsProps) => emitNotification(t, 'error', c)),
  info: withToast((t, c: ArgsProps) => emitNotification(t, 'info', c)),
  warning: withToast((t, c: ArgsProps) => emitNotification(t, 'warning', c)),
  open: withToast((t, c: ArgsProps) => emitNotification(t, c.type, c)),
  destroy: dismissFor('notification'),
}
